<script setup>

import {nextTick} from 'vue';

import SearchPill from "@/components/common/other/SearchPill";
import API from "@/classes/global/API";
import DropDownItem from "@/classes/component-classes/DropDownItem";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StockSearchItem from "@/classes/stock/StockSearchItem";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import DateInput from "@/components/common/input/DateInput.vue";

import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";

const emits = defineEmits(['search', 'clear']);
const props = defineProps({modelValue: {}});

function handleSearch(searchPill) {

  nextTick(async () => {

    props.modelValue.isSearchData = useCheckSearchCriteriaExists(props.modelValue.searchCriteria);

    if (props.modelValue.isSearchData) {

      emits('search');

      const response = await API.searchPickstockShipments(props.modelValue.searchCriteria);

      if (response) {

        props.modelValue.searchData = response;

        let searchHistoryItemExists;

        if (props.modelValue.searchCriteria[searchPill]) {

          searchHistoryItemExists = props.modelValue.searchPills[searchPill]
              .history?.find((item) => item.value === props.modelValue.searchCriteria[searchPill]);

          if (!searchHistoryItemExists && response.length > 0) props.modelValue.searchPills[searchPill].history
              ?.unshift(new DropDownItem(props.modelValue.searchPills[searchPill].history.length + 1,
                  props.modelValue.searchCriteria[searchPill]));
        }

        if (response.length === 0) props.modelValue.isSearchData = false;
      }

      return

    }

  })

}

function handleClearSearch(searchPill) {

  // Move the events along to ensure clearing of the box has happened before data present check
  nextTick(() => {

    let searchCriteriaExists = useCheckSearchCriteriaExists(props.modelValue.searchCriteria);

    if(searchCriteriaExists) return handleSearch(searchPill);

    handleClearAllFields()

  })

}

function handleClearAllFields() {

  props.modelValue.searchCriteria = new StockSearchItem();
  emits('clear');
}

</script>

<template>
  <div class="cmp-search-stock-pill-group_search-area">
    <div v-if="!props.modelValue.searchPillGroupTwo" class="cmp-search-stock-pill-group_search-pill-container">
      <SearchPill
          @search="handleSearch('warehouseRef')"
          :enabled="props.modelValue.searchPills.warehouseRef.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="250"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Warehouse Ref"
              :label-top="true"
              @clear="handleClearSearch('warehouseRef')"
              @select="handleSearch('warehouseRef')"
              @keydown.enter="handleSearch('warehouseRef')"
              :dataSource="props.modelValue.searchPills.warehouseRef.history"
              v-model="props.modelValue.searchCriteria.warehouseRef"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuNumber')"
          :enabled="props.modelValue.searchPills.skuNumber.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="250"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Number"
              :label-top="true"
              @clear="handleClearSearch('skuNumber')"
              @select="handleSearch('skuNumber')"
              @keydown.enter="handleSearch('skuNumber')"
              :dataSource="props.modelValue.searchPills.skuNumber.history"
              v-model="props.modelValue.searchCriteria.skuNumber"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('jobRef')"
          :enabled="props.modelValue.searchPills.jobRef.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="260"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Job Ref"
              :label-top="true"
              @clear="handleClearSearch('jobRef')"
              @select="handleSearch('jobRef')"
              @keydown.enter="handleSearch('jobRef')"
              :dataSource="props.modelValue.searchPills.jobRef.history"
              v-model="props.modelValue.searchCriteria.jobRef"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('customerRef')"
          :enabled="props.modelValue.searchPills.customerRef.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="250"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Customer Ref"
              :label-top="true"
              @clear="handleClearSearch('customerRef')"
              @select="handleSearch('customerRef')"
              @keydown.enter="handleSearch('customerRef')"
              :dataSource="props.modelValue.searchPills.customerRef.history"
              v-model="props.modelValue.searchCriteria.customerRef"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuName')"
          :enabled="props.modelValue.searchPills.skuName.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="250"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Name"
              :label-top="true"
              @clear="handleClearSearch('skuName')"
              @select="handleSearch('skuName')"
              @keydown.enter="handleSearch('skuName')"
              :dataSource="props.modelValue.searchPills.skuName.history"
              v-model="props.modelValue.searchCriteria.skuName"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
    </div>
    <div v-else class="cmp-search-stock-pill-group_search-pill-container">
      <SearchPill
          @search="handleSearch('deliverTo')"
          :enabled="props.modelValue.searchPills.deliverTo.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="240"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Deliver To"
              :label-top="true"
              @clear="handleClearSearch('deliverTo')"
              @select="handleSearch('deliverTo')"
              @keydown.enter="handleSearch('deliverTo')"
              :dataSource="props.modelValue.searchPills.deliverTo.history"
              v-model="props.modelValue.searchCriteria.deliverTo"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('soNumber')"
          :enabled="props.modelValue.searchPills.soNumber.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SO Number"
              :label-top="true"
              @clear="handleClearSearch('soNumber')"
              @select="handleSearch('soNumber')"
              @keydown.enter="handleSearch('soNumber')"
              :dataSource="props.modelValue.searchPills.soNumber.history"
              v-model="props.modelValue.searchCriteria.soNumber"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('releasedBy')"
          :enabled="props.modelValue.searchPills.releasedBy.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="240"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Released By"
              :label-top="true"
              @clear="handleClearSearch('releasedBy')"
              @select="handleSearch('releasedBy')"
              @keydown.enter="handleSearch('releasedBy')"
              :dataSource="props.modelValue.searchPills.releasedBy.history"
              v-model="props.modelValue.searchCriteria.releasedBy"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('releaseDateFrom')"
          :enabled="props.modelValue.searchPills.releaseDateFrom.enabled">
        <template #control>
          <DateInput
              v-model="props.modelValue.searchCriteria.releaseDateFrom"
              :clearable="true"
              :width="150"
              :label-top="true"
              label="Release Date From">
            <el-date-picker
                :clearable="false"
                style="margin-right: 10px"
                v-model="props.modelValue.searchCriteria.releaseDateFrom"
                value-format="DD-MMM-YY"/>
          </DateInput>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('releaseDateTo')"
          :enabled="props.modelValue.searchPills.releaseDateTo.enabled">
        <template #control>
          <DateInput
              v-model="props.modelValue.searchCriteria.releaseDateTo"
              :clearable="true"
              :width="150"
              :label-top="true"
              label="Release Date To">
            <el-date-picker
                :clearable="false"
                style="margin-right: 10px"
                v-model="props.modelValue.searchCriteria.releaseDateTo"
                value-format="DD-MMM-YY"/>
          </DateInput>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuGroup')"
          :enabled="props.modelValue.searchPills.skuGroup.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="278"
              :maxLength="50"
              placeholder="Select"
              :allowCustomValue="false"
              :disableFilter="true"
              label="SKU Group"
              :label-top="true"
              @clear="handleClearSearch('skuGroup')"
              @select="handleSearch('skuGroup')"
              v-model="props.modelValue.searchCriteria.skuGroup"
              :data-source="props.modelValue.skuGroups"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
    </div>
    <div class="cmp-search-stock-pill-group_clear-button">
      <StaticTooltip :icon="false">
        <template #content>
          Clear All
        </template>
        <i class="fa-solid fa-trash-can-list" style="color:#4c4c4c; cursor: pointer;"
           @click="handleClearAllFields"></i>
      </StaticTooltip>
    </div>
  </div>

</template>

<style lang="scss" scoped>

.cmp-search-stock-pill-group {

  &_search-area {
    display: flex;
    align-items: center;
    padding-left: 10px;
    height: 90px;
    //margin-left: 10px;
    background-color: #ffffff;
    border-radius: var(--rounded-corners);
    border: 1px solid var(--border-color);
    border-left: 6px solid var(--left-border-highlight);
  }

  &_search-pill-container {
    display: flex;
    align-items: center;
    height: 80px;
    gap: 20px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 5px 0 5px;
  }

  &_clear-button {
    display: flex;
    align-items: center;
    margin-left: 30px;
  }

}
</style>
